import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { useMock } from '../Shell/mockContext';

/* useHiglos({ owningFamilies, accountUUID, primaryFamilyID })

   Returns a memoised map of Higlos the user has visibility into.

   Shape (matches MockProvider.higlos):
     { [iglooID]: {
         iglooID,
         name,                 // best guess display name (primary family's label first, then any other family's label, then iglooInfo.name)
         controllers,          // raw iglooInfo.controllers array (familyIDs)
         members,              // raw iglooInfo.members array (familyIDs)
         ownedMemberships: [{ familyID, familyName, position, isMember }], // every owned family — isMember=false means the family is NOT in this Higlo yet
         externalFamilies: [{ familyID, name, status, isController }], // other families (pending or accepted)
       }
     }

   Implementation notes:
   - We walk every owning family's `familyInfo/{fid}/igloos` subcollection
     (live). Each iglooID encountered becomes (or merges into) a Higlo entry.
   - For each iglooID we also live-subscribe to `iglooInfo/{iglooID}` to get
     the controllers/members/pending arrays so we can render "external"
     families (anything NOT in owningFamilies).
   - External family display names prefer the sender's private label stored
     in `familyInfo/{ownedFID}/connectedFamilies/{extFID}.name`. That's the
     "knownName" the sender picked when inviting — useful before the
     invitee's own `familyInfo.name` is readable (e.g. pending invites) and
     for consistency with the OLD webapp's display. Falls back to
     `familyInfo/{extFID}.name` if no connectedFamilies entry exists, then
     to a short UUID suffix.

   Cleanup: all listeners are torn down when owningFamilies changes or on
   unmount. Mock-mode short-circuits and returns the mock's `higlos` map.
*/
export function useHiglos({ owningFamilies, accountUUID, primaryFamilyID }) {
  const mock = useMock();

  // Per-owning-family igloo membership state.
  // { [familyID]: { familyName, links: [{ iglooID, name, position }] } }
  const [familyMap, setFamilyMap] = useState({});
  // Per-iglooID snapshot of iglooInfo.
  // { [iglooID]: { controllers: [], members: [], pending: [fid...], name } }
  const [iglooInfoMap, setIglooInfoMap] = useState({});
  // Per-family (external) display names pulled lazily from familyInfo/{fid}.
  // { [familyID]: name }
  const [externalNames, setExternalNames] = useState({});
  // Per-owned-family connectedFamilies map: the user's private labels for
  // families they've invited. Keyed { [ownedFID]: { [extFID]: name } }.
  // Preferred over externalNames for display — matches OLD behaviour.
  const [connectedNames, setConnectedNames] = useState({});

  const owningKey = useMemo(
    () => (owningFamilies ? owningFamilies.slice().sort().join('|') : ''),
    [owningFamilies],
  );

  /* Subscribe to each owning family: name + igloos subcollection. */
  useEffect(() => {
    if (mock?.higlos) return undefined;
    if (!owningFamilies || owningFamilies.length === 0) {
      setFamilyMap({});
      return undefined;
    }
    const unsubs = [];
    for (const fid of owningFamilies) {
      unsubs.push(
        onSnapshot(
          doc(db, 'familyInfo', fid),
          (snap) => {
            if (!snap.exists()) return;
            const name = snap.data().name || '';
            setFamilyMap((prev) => ({
              ...prev,
              [fid]: { ...(prev[fid] || { links: [] }), familyName: name },
            }));
          },
          (err) => console.warn('useHiglos familyInfo listener', fid, err.code),
        ),
      );
      unsubs.push(
        onSnapshot(
          collection(db, 'familyInfo', fid, 'igloos'),
          (snap) => {
            const links = snap.docs.map((d) => ({
              iglooID: d.id,
              ...d.data(),
            }));
            setFamilyMap((prev) => ({
              ...prev,
              [fid]: { ...(prev[fid] || { familyName: '' }), links },
            }));
          },
          (err) => console.warn('useHiglos igloos listener', fid, err.code),
        ),
      );
      // connectedFamilies — the user's private labels for families they've
      // invited. Preferred over the invitee's own familyInfo.name for
      // display, matching the OLD webapp.
      unsubs.push(
        onSnapshot(
          collection(db, 'familyInfo', fid, 'connectedFamilies'),
          (snap) => {
            const entry = {};
            snap.docs.forEach((d) => {
              const data = d.data() || {};
              if (data.name) entry[d.id] = data.name;
            });
            setConnectedNames((prev) => ({ ...prev, [fid]: entry }));
          },
          (err) => {
            if (err?.code === 'permission-denied') return;
            console.warn('useHiglos connectedFamilies listener', fid, err.code);
          },
        ),
      );
    }
    return () => {
      unsubs.forEach((u) => {
        try { u(); } catch (_) {}
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owningKey, mock]);

  /* From the current familyMap, derive the set of iglooIDs we need to
     subscribe to for iglooInfo. Subscribe / unsubscribe when it changes. */
  const iglooIDs = useMemo(() => {
    const set = new Set();
    Object.values(familyMap).forEach((f) => {
      (f.links || []).forEach((l) => set.add(l.iglooID));
    });
    return Array.from(set).sort();
  }, [familyMap]);

  const iglooKey = iglooIDs.join('|');

  useEffect(() => {
    if (mock?.higlos) return undefined;
    if (iglooIDs.length === 0) {
      setIglooInfoMap({});
      return undefined;
    }
    const unsubs = [];
    for (const iglooID of iglooIDs) {
      // Top-level doc (controllers, members, name)
      unsubs.push(
        onSnapshot(
          doc(db, 'iglooInfo', iglooID),
          (snap) => {
            if (!snap.exists()) {
              setIglooInfoMap((prev) => {
                const next = { ...prev };
                delete next[iglooID];
                return next;
              });
              return;
            }
            const d = snap.data();
            setIglooInfoMap((prev) => ({
              ...prev,
              [iglooID]: {
                ...(prev[iglooID] || { pending: [] }),
                controllers: d.controllers || [],
                members: d.members || [],
                name: d.name || '',
              },
            }));
          },
          (err) => console.warn('useHiglos iglooInfo listener', iglooID, err.code),
        ),
      );
      // Pending subcollection — IDs here are pending familyIDs. We keep both
      // the flat list (for existing set-membership checks) and a keyed map of
      // doc data so rows can show "Invited X ago" and know the intended role.
      unsubs.push(
        onSnapshot(
          collection(db, 'iglooInfo', iglooID, 'pending'),
          (snap) => {
            const pending = snap.docs.map((d) => d.id);
            const pendingData = {};
            snap.docs.forEach((d) => { pendingData[d.id] = d.data() || {}; });
            setIglooInfoMap((prev) => ({
              ...prev,
              [iglooID]: {
                ...(prev[iglooID] || { controllers: [], members: [], name: '' }),
                pending,
                pendingData,
              },
            }));
          },
          (err) => {
            if (err?.code === 'permission-denied') return;
            console.warn('useHiglos pending listener', iglooID, err.code);
          },
        ),
      );
    }
    return () => {
      unsubs.forEach((u) => {
        try { u(); } catch (_) {}
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iglooKey, mock]);

  /* Figure out which external (non-owned) families we need names for, and
     subscribe to them lazily. We only add — we never unsubscribe mid-run,
     because names are stable for the life of the session. */
  useEffect(() => {
    if (mock?.higlos) return undefined;
    const owning = new Set(owningFamilies || []);
    const wanted = new Set();
    Object.values(iglooInfoMap).forEach((info) => {
      (info.controllers || []).forEach((fid) => {
        if (!owning.has(fid)) wanted.add(fid);
      });
      (info.members || []).forEach((fid) => {
        if (!owning.has(fid)) wanted.add(fid);
      });
      (info.pending || []).forEach((fid) => {
        if (!owning.has(fid)) wanted.add(fid);
      });
    });
    const toAdd = Array.from(wanted).filter((fid) => !(fid in externalNames));
    if (toAdd.length === 0) return undefined;
    const unsubs = toAdd.map((fid) =>
      onSnapshot(
        doc(db, 'familyInfo', fid),
        (snap) => {
          if (snap.exists()) {
            const n = snap.data().name || '';
            setExternalNames((prev) => ({ ...prev, [fid]: n }));
          }
        },
        (err) => {
          if (err?.code === 'permission-denied') return;
          console.warn('useHiglos external name listener', fid, err.code);
        },
      ),
    );
    return () => {
      unsubs.forEach((u) => {
        try { u(); } catch (_) {}
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iglooInfoMap, owningKey, mock]);

  /* Compose the final map. Mock wins when present. */
  return useMemo(() => {
    if (mock?.higlos) return mock.higlos;
    const owning = new Set(owningFamilies || []);
    const out = {};
    // Seed from owned memberships — every iglooID our own families touch.
    Object.entries(familyMap).forEach(([familyID, fam]) => {
      (fam.links || []).forEach((link) => {
        const iglooID = link.iglooID;
        if (!out[iglooID]) {
          out[iglooID] = {
            iglooID,
            name: '',
            controllers: [],
            members: [],
            ownedMemberships: [],
            externalFamilies: [],
          };
        }
        out[iglooID].ownedMemberships.push({
          familyID,
          familyName: fam.familyName || '',
          iglooLabel: link.name || '',   // the family's label for this Higlo
          position: link.position || null,
          isMember: true,
        });
      });
    });
    // For each Higlo, fill in the user's OTHER owned families that are NOT
    // currently in the igloo. These render dimmer with a "+ Add" affordance so
    // the user can pull them into the Higlo. We do this for every Higlo the
    // user has visibility into (i.e. at least one of their families is in it).
    Object.values(out).forEach((entry) => {
      const present = new Set(entry.ownedMemberships.map((m) => m.familyID));
      (owningFamilies || []).forEach((fid) => {
        if (present.has(fid)) return;
        const fam = familyMap[fid] || {};
        entry.ownedMemberships.push({
          familyID: fid,
          familyName: fam.familyName || '',
          iglooLabel: '',
          position: null,
          isMember: false,
        });
      });
    });
    // Display-name pick: prefer the primary family's per-family Higlo label
    // (`familyInfo/{primaryFID}/igloos/{iglooID}.name`); if the primary isn't
    // a member or hasn't named it, fall back to the first other owned family
    // that has a label; then `iglooInfo.name`; then "Higlo".
    Object.values(out).forEach((entry) => {
      const info = iglooInfoMap[entry.iglooID];
      const primaryLabel = primaryFamilyID
        ? entry.ownedMemberships.find(
            (m) => m.familyID === primaryFamilyID && m.iglooLabel,
          )?.iglooLabel
        : null;
      const fallbackLabel = entry.ownedMemberships.find((m) => m.iglooLabel)?.iglooLabel;
      entry.name = primaryLabel || fallbackLabel || info?.name || 'Higlo';
      if (info) {
        entry.controllers = info.controllers || [];
        entry.members = info.members || [];
        const externals = [];
        const ctrlSet = new Set(info.controllers || []);
        const pendingSet = new Set(info.pending || []);
        const all = new Set([
          ...(info.controllers || []),
          ...(info.members || []),
          ...(info.pending || []),
        ]);
        const pendingData = info.pendingData || {};
        all.forEach((fid) => {
          if (owning.has(fid)) return;
          // Prefer the sender's private label in connectedFamilies. Look
          // across every owned family until we find a hit — the nickname
          // might have been written by any controller on this account.
          let nickname = null;
          for (const ownedFID of owningFamilies || []) {
            const n = connectedNames[ownedFID]?.[fid];
            if (n) { nickname = n; break; }
          }
          const name = nickname || externalNames[fid] || shortID(fid);
          const isPending = pendingSet.has(fid);
          externals.push({
            familyID: fid,
            name,
            status: isPending ? 'pending' : 'accepted',
            isController: ctrlSet.has(fid),
            invitedAt: isPending ? pendingData[fid]?.invitedAt : undefined,
          });
        });
        entry.externalFamilies = externals;
      }
    });
    return out;
  }, [familyMap, iglooInfoMap, externalNames, connectedNames, owningFamilies, primaryFamilyID, mock]);
}

function shortID(fid) {
  if (!fid) return 'Family';
  return `Family ${fid.slice(0, 6)}`;
}
